import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ADMIN_TOKEN_KEY = 'aitos_admin_token';
const ADMIN_USER_KEY = 'aitos_admin_user';

export interface AdminUser {
  id: number;
  username: string;
  name: string;
}

interface AdminAuthState {
  token: string | null;
  admin: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AdminAuthContextValue extends AdminAuthState {
  login: (token: string, admin: AdminUser) => void;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AdminAuthState>({
    token: null,
    admin: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const adminStr = localStorage.getItem(ADMIN_USER_KEY);
    if (token && adminStr) {
      try {
        const admin = JSON.parse(adminStr) as AdminUser;
        setState({ token, admin, isAuthenticated: true, isLoading: false });
      } catch {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_USER_KEY);
        setState({ token: null, admin: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, []);

  const login = useCallback((token: string, admin: AdminUser) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(admin));
    setState({ token, admin, isAuthenticated: true, isLoading: false });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setState({ token: null, admin: null, isAuthenticated: false, isLoading: false });
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) return { Authorization: `Bearer ${token}` };
    return {} as Record<string, string>;
  }, []);

  const value: AdminAuthContextValue = {
    ...state,
    login,
    logout,
    getAuthHeaders,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
}
